// ============================================================
// 📄 GERADOR DA FICHA DE HEPATITE B (B211, AI_97) — "Formulário de
//    Solicitação de Medicamentos – Hepatite B" (PCDT, SETEMBRO/2023)
// ============================================================
// Reproduz, em HTML imprimível FIEL ao formulário oficial do Ministério da
// Saúde (campos 1–37, cantos arredondados), a ficha exigida para dispensação
// dos medicamentos contra o vírus da hepatite B no SUS (Alfapeginterferona 2a,
// Entecavir, Tenofovir TDF, Tenofovir Alafenamida TAF e Imunoglobulina humana
// anti-hepatite B).
//
// Como o LME, é disparada pelo RECEITUÁRIO (por MEDICAMENTO) e sai JUNTO com a
// receita, no MESMO documento. O médico preenche, pelo modal, todos os campos
// 1–33 (a própria ficha exige); muitos são semeados do prontuário e das
// sorologias (HBV-DNA, HBeAg, HBsAg, anti-HBc, anti-HDV IgG, HDV-RNA).
//
// 100% client-side, sem biblioteca de PDF e sem logar conteúdo clínico (LGPD).

import { Paciente } from "../../../types";
import { abrirPDFEmNovaJanela } from "./gerarPDF";
import { escapeHtml } from "./gerarFichaGAL";
import { SolicitanteCargaViral } from "./gerarFichaCargaViral";
import { MEDICAMENTOS_HBV_CAMPO34 } from "../data/formulariosAuxiliares";

// Reusa o solicitante (mesmos campos do cadastro: profissional + unidade).
export type SolicitanteHepatiteB = SolicitanteCargaViral;

// ------------------------------------------------------------
// OPÇÕES OFICIAIS DOS CAMPOS
// ------------------------------------------------------------
export const CIDS_HBV = [
  "B16.0",
  "B16.1",
  "B16.2",
  "B16.9",
  "B17.0",
  "B18.0",
  "B18.1",
] as const;

export const VALIDADE_OPCOES = [
  "30 dias",
  "60 dias",
  "90 dias",
  "120 dias",
  "150 dias",
  "180 dias",
] as const;

export const COINFECCOES_OPCOES = ["HIV", "Hepatite C", "Hepatite Delta"] as const;

export const INDICACOES_TERAPEUTICAS = [
  "Tratamento da Hepatite B",
  "Prevenção da Transmissão vertical",
  "Prevenção da reativação viral",
  "Profilaxia da reinfecção pós-transplante hepático",
  "Profilaxia do profissional de saúde",
] as const;

export const SITUACAO_PROFILAXIA_OPCOES = [
  "Terapia imunossupressora ou quimioterapia",
  "Candidatos a transplante de órgãos sólidos",
] as const;

export const RISCO_REATIVACAO_OPCOES = ["Alto", "Moderado", "Baixo"] as const;

export const MOMENTO_PROFILAXIA_OPCOES = [
  "Pré-transplante hepático",
  "Pós-transplante hepático",
] as const;

export const RISCO_REINFECCAO_OPCOES = [
  "Alto risco",
  "Baixo risco",
  "Muito baixo risco",
] as const;

export const BETA_HCG_OPCOES = [
  "Positivo",
  "Negativo",
  "Uso de métodos contraceptivos de alta eficácia ou definitivos",
] as const;

// Campo 29 — outros critérios para indicação de tratamento (múltipla escolha)
export const OUTROS_CRITERIOS_OPCOES = [
  "História familiar de carcinoma hepatocelular (CHC);",
  "Manifestações extra-hepáticas, como artrite, vasculites, nefropatia poliarterite nodosa, neuropatia periférica;",
  "Hepatite aguda grave (INR > 1,5; icterícia por mais de 4 semanas, encefalopatia ou ascite);",
  "Reativação de hepatite B crônica;",
  "Doença hepática avançada;",
  "Hepatite fulminante",
  "Paciente pediátrico (<18 anos) com ALT >=1,3x LSN por mais de 6 meses",
  "Paciente pediátrico com sinais clínicos, ultrassonográficos e endoscópicos de cirrose.",
  "Estadiamento de fibrose em zona cinzenta com critério clínico associado a um pior prognóstico da hepatite B crônica (sexo masculino, idade > 40 anos, história familiar de cirrose por HBV ou por causa desconhecida, ou presença de outras causas de hepatopatia);",
  "Biópsia hepática METAVIR maior ou igual a A2 e ou F2",
  "Elastografia hepática: EHT >9 kPa (níveis normais de ALT) ou >12 kPa (ALT entre 1 e 5x LSN), ou pSWE/ARFI ≥1,8 m/seg, ou 2D-SWE >10 kPa",
  "Profissional de saúde com risco aumentado de transmissão do HBV",
  "Não apresenta.",
] as const;

// Campo 30 — o paciente apresenta alguma das seguintes condições? (múltipla)
export const CONDICOES_OPCOES = [
  "Consumo atual de álcool e/ou drogas;",
  "Cardiopatia ou pneumopatia grave;",
  "Disfunção tireoidiana não controlada;",
  "Distúrbios psiquiátricos não tratados;",
  "Diabetes melito com histórico de crise hiperglicêmica aguda",
  "Osteoporose e outras doenças do metabolismo ósseo",
  "Antecedente de transplante, exceto hepático",
  "Distúrbios hematológicos: anemia, leucopenia, plaquetopenia",
  "Doença auto-imune",
  "Alteração da função renal por uso prévio de tenofovir",
  "Doença renal crônica",
  "Neoplasia recente",
  "Exacerbação aguda de hepatite viral",
  "Insuficiência hepática grave",
  "Hepatite auto-imune",
  "Nenhuma",
] as const;

// Campo 31 — contraindicação ao uso de (múltipla)
export const CONTRAINDICACAO_OPCOES = [
  "Alfapeginterferona",
  "Tenofovir (TDF)",
  "Entecavir",
  "TAF",
  "Sem contraindicação",
] as const;

// Campo 32 — apresentou resistência ou falha terapêutica ao uso de (múltipla)
export const RESISTENCIA_OPCOES = [
  "Alfapeginterferona",
  "Tenofovir",
  "Entecavir",
  "TAF",
  "Nenhuma",
] as const;

// Campo 33 — uso prévio de (múltipla)
export const USO_PREVIO_OPCOES = [
  "Alfapeginterferona",
  "Tenofovir",
  "Entecavir",
  "TAF",
  "Lamivudina",
  "Nenhum (Virgem de tratamento)",
] as const;

// ------------------------------------------------------------
// DADOS PREENCHIDOS NO MODAL (persistidos junto à receita)
// ------------------------------------------------------------
export interface DadosHepatiteB {
  // Identificação (campos 3/4/6) — os demais vêm do prontuário
  sinan: string; // campo 3
  prontuario: string; // campo 4
  nomeSocial: string; // campo 6
  // Clínicos
  inicioTratamento: "Sim" | "Não" | ""; // campo 7
  validadePor: string; // campo 8
  origemAcompanhamento: "Público" | "Privado" | ""; // campo 9
  pesoPaciente: string; // campo 10 (kg)
  cid10: string; // campo 11 — um dos CIDS_HBV
  semCoinfeccao: boolean; // campo 12
  coinfeccoes: string[]; // campo 12 (HIV/Hepatite C/Hepatite Delta)
  hbeag: "Reagente" | "Não Reagente" | ""; // campo 13
  alt: "Maior que 1,5 vezes LSN" | "Menor que 1,5 vezes LSN" | ""; // campo 14
  hbvDnaResultado: string; // campo 15 (UI/mL)
  hbvDnaData: string; // campo 15 (data)
  gravidadeCirrose: "Sem Cirrose" | "Child A" | "Child B ou C" | ""; // campo 16
  antiHDV: "Reagente" | "Não Reagente" | ""; // campo 17
  hdvRna: "Detectável" | "Não detectável" | ""; // campo 18
  betaHCG: string; // campo 19
  diagMaternoData: string; // campo 20
  diagMaternoIG: string; // campo 20 (semanas)
  indicacaoTerapeutica: string; // campo 21
  situacaoProfilaxia: string; // campo 22
  doadorHBsAg: "Reagente" | "Não Reagente" | ""; // campo 23
  doadorAntiHBc: "Reagente" | "Não Reagente" | ""; // campo 23
  receptorHBsAg: "Reagente" | "Não Reagente" | ""; // campo 24
  receptorAntiHBc: "Reagente" | "Não Reagente" | ""; // campo 24
  riscoReativacao: string; // campo 25
  momentoProfilaxia: string; // campo 26
  riscoReinfeccao: string; // campo 27
  terapiaPreemptiva: "Sim" | "Não" | ""; // campo 28
  outrosCriterios: string[]; // campo 29
  condicoes: string[]; // campo 30
  contraindicacao: string[]; // campo 31
  resistenciaFalha: string[]; // campo 32
  usoPrevio: string[]; // campo 33
  medicamentos: string[]; // campo 34 — ids de MEDICAMENTOS_HBV_CAMPO34 marcados
  entecavirComprimidos: string; // campo 34 — nº de comprimidos do Entecavir
  observacao: string; // campo 35
  dataSolicitacao: string; // campo 36 (dd/mm/aaaa)
}

// ------------------------------------------------------------
// HELPERS DE MARCAÇÃO
// ------------------------------------------------------------
// B289 (AI_127) — a ficha mostra SÓ a opção marcada no modal (economiza espaço;
// os glyphs ◉/▣ de marcação não renderizam na fonte da ficha). Exceção: os
// campos 29/30 (listas longas) vêm COMPLETOS, com as marcadas em destaque
// (negrito + ■/□, que renderizam) — ver listaCheck.
const sel = (valor: string | undefined): string =>
  valor ? escapeHtml(valor) : "&nbsp;";
const selMulti = (valores: string[]): string =>
  valores && valores.length ? escapeHtml(valores.join("; ")) : "&nbsp;";

// Caixa com rótulo "N - LABEL" sobreposto à borda superior (cantos arredondados).
interface OpcoesBox {
  estilo?: string; // grid-column/area explícito
  obrig?: boolean;
  classe?: string;
}
function box(num: string, rotulo: string, conteudo: string, opts: OpcoesBox = {}): string {
  const { estilo = "", obrig = false, classe = "" } = opts;
  return `
    <div class="hb-box ${classe}" style="${estilo}">
      <div class="hb-num">${num ? `${num} - ` : ""}${rotulo}${obrig ? '<span class="hb-ob">*</span>' : ""}</div>
      <div class="hb-cont">${conteudo}</div>
    </div>`;
}

// ------------------------------------------------------------
// MIOLO DA FICHA (sem <html>/<style>/@page)
// ------------------------------------------------------------
function corpoHepatiteB(
  paciente: Paciente,
  solicitante: SolicitanteHepatiteB,
  dados: DadosHepatiteB,
  // B289 (AI_127) — selo ICP-Brasil (compacto) no campo 36 (assinatura do
  // prescritor), só no PDF assinado. Sem selo = espaço em branco (assinar à mão).
  selo?: string
): string {
  const cnsPaciente = paciente.cns || paciente.hospub || "";
  const reg = solicitante.registroConselho || "";

  // Campo 1 — CNS em caixas (até 15 dígitos como o cartão SUS)
  const cnsBoxes = (() => {
    const dig = (cnsPaciente.match(/\d/g) || []).join("");
    const n = Math.max(15, dig.length);
    let s = "";
    for (let i = 0; i < n; i++) s += `<span class="hb-cel">${dig[i] || "&nbsp;"}</span>`;
    return `<div class="hb-celulas">${s}</div>`;
  })();

  // Campo 34 — medicamentos com posologia; marca os prescritos.
  const linhasMed = MEDICAMENTOS_HBV_CAMPO34.map((m) => {
    const marcado = dados.medicamentos.includes(m.id);
    let poso = m.posologia;
    if (m.id === "entecavir" && dados.entecavirComprimidos) {
      poso = poso.replace("_____", escapeHtml(dados.entecavirComprimidos));
    }
    return `
      <tr>
        <td class="hb-med-ck"><span class="hb-cx2${marcado ? " on" : ""}"></span></td>
        <td class="hb-med-nome"><b>${escapeHtml(m.rotulo)}</b><div class="hb-med-poso">${escapeHtml(poso)}</div></td>
        <td class="hb-med-disp">
          <div>Quantidade dispensada</div>
          <div class="hb-med-disp-l">1ª disp ____ &nbsp; 2ª disp ____ &nbsp; 3ª disp ____ &nbsp; 4ª disp ____ &nbsp; 5ª disp ____ &nbsp; 6ª disp ____</div>
        </td>
      </tr>`;
  }).join("");

  // Campos 29/30 — listas longas COMPLETAS (não desfigurar a ficha); a opção
  // marcada no modal sai em destaque: caixinha CSS preenchida (renderiza
  // garantido, sem depender de glyph) + negrito; as demais com caixinha vazia.
  const listaCheck = (opcoes: readonly string[], marcados: string[]): string =>
    `<div class="hb-lista">${opcoes
      .map((o) => {
        const m = marcados.includes(o);
        const caixa = `<span class="hb-cx2${m ? " on" : ""}"></span>`;
        return `<div class="hb-lista-item">${caixa}${
          m ? `<b>${escapeHtml(o)}</b>` : escapeHtml(o)
        }</div>`;
      })
      .join("")}</div>`;

  return `
  <div class="hb">
    <!-- TÍTULO -->
    <div class="hb-titulo">
      <div class="hb-titulo-t">Formulário de Solicitação de Medicamentos – Hepatite B</div>
      <div class="hb-titulo-s">Para solicitação de medicamentos, o preenchimento dos campos de 1 a 33 deve ser realizado obrigatoriamente pelo médico.</div>
      <div class="hb-titulo-d">SETEMBRO/2023</div>
    </div>

    <!-- IDENTIFICAÇÃO -->
    <div class="hb-grade hb-g-ident">
      ${box("1", "CNS – Cartão Nacional de Saúde", cnsBoxes, { estilo: "grid-column: 1 / span 5;" })}
      ${box("2", "CPF", escapeHtml(paciente.cpf || ""), { estilo: "grid-column: 6 / span 3;", obrig: true })}
      ${box("3", "SINAN", escapeHtml(dados.sinan), { estilo: "grid-column: 9 / span 2;" })}
      ${box("4", "Prontuário", escapeHtml(dados.prontuario), { estilo: "grid-column: 11 / span 2;" })}
    </div>
    <div class="hb-grade">
      ${box("5", "Nome Completo do Usuário - Civil", escapeHtml(paciente.nome), { estilo: "grid-column: 1 / span 12;", obrig: true })}
    </div>
    <div class="hb-grade">
      ${box("6", "Nome Social", escapeHtml(dados.nomeSocial), { estilo: "grid-column: 1 / span 12;" })}
    </div>

    <!-- LINHA 7/8/9/10 (respostas em destaque) -->
    <div class="hb-grade">
      ${box("7", "Início de tratamento?", sel(dados.inicioTratamento), { estilo: "grid-column: 1 / span 3;", obrig: true, classe: "hb-grande" })}
      ${box("8", "Este formulário é válido por:", sel(dados.validadePor), { estilo: "grid-column: 4 / span 3;", obrig: true, classe: "hb-grande" })}
      ${box("9", "Origem do acompanhamento médico", sel(dados.origemAcompanhamento), { estilo: "grid-column: 7 / span 2;", obrig: true, classe: "hb-grande" })}
      ${box("10", "Peso (kg):", `<span class="hb-val">${escapeHtml(dados.pesoPaciente) || "&nbsp;"}</span>`, { estilo: "grid-column: 9 / span 2;", obrig: true, classe: "hb-grande" })}
      ${box("11", "CID-10", sel(dados.cid10), { estilo: "grid-column: 11 / span 2;", obrig: true })}
    </div>

    <!-- LINHA 12/13/14/15/16 -->
    <div class="hb-grade">
      ${box("12", "Paciente coinfectado?", dados.semCoinfeccao ? "Sem Coinfecção" : selMulti(dados.coinfeccoes), { estilo: "grid-column: 1 / span 4;", obrig: true })}
      ${box("13", "Exame de HBeAg:", sel(dados.hbeag), { estilo: "grid-column: 5 / span 2;", obrig: true })}
      ${box("14", "Exame de ALT?", sel(dados.alt), { estilo: "grid-column: 7 / span 2;", obrig: true })}
      ${box("15", "Exame de HBV-DNA:", `Resultado: ${escapeHtml(dados.hbvDnaResultado)} UI/mL<br/>Data: ${escapeHtml(dados.hbvDnaData)}`, { estilo: "grid-column: 9 / span 2;", obrig: true })}
      ${box("16", "Gravidade de cirrose (Child-Pugh)", sel(dados.gravidadeCirrose), { estilo: "grid-column: 11 / span 2;", obrig: true })}
    </div>

    <!-- LINHA 17/18/19/20 -->
    <div class="hb-grade">
      ${box("17", "Exame de anti-HDV IgG:", `<div class="hb-mini">(obrigatório para CIDs B16.0, B16.1, B17.0 e B18.0)</div>${sel(dados.antiHDV)}`, { estilo: "grid-column: 1 / span 3;", classe: "hb-medio" })}
      ${box("18", "Exame de HDV-RNA:", `<div class="hb-mini">(obrigatório para CIDs B16.0, B16.1, B17.0 e B18.0)</div>${sel(dados.hdvRna)}`, { estilo: "grid-column: 4 / span 3;", classe: "hb-medio" })}
      ${box("19", "Resultado Beta-HCG?", sel(dados.betaHCG), { estilo: "grid-column: 7 / span 3;", obrig: true })}
      ${box("20", "Diagnóstico Materno (apenas se beta-HCG Positivo):", `Data do diagnóstico: ${escapeHtml(dados.diagMaternoData)}<br/>Idade gestacional: ${escapeHtml(dados.diagMaternoIG)} semanas de gestação`, { estilo: "grid-column: 10 / span 3;" })}
    </div>

    <!-- CAMPO 21 -->
    <div class="hb-grade">
      ${box("21", "Indicação terapêutica:", sel(dados.indicacaoTerapeutica), { estilo: "grid-column: 1 / span 12;", obrig: true })}
    </div>

    <!-- LINHA 22/23/24/25-26 -->
    <div class="hb-grade">
      ${box("22", "Situação para profilaxia da reativação viral:", `<div class="hb-mini">(obrigatório se prevenção da reativação viral)</div>${sel(dados.situacaoProfilaxia)}`, { estilo: "grid-column: 1 / span 3;" })}
      ${box("23", "Doador apresenta:", `<div class="hb-mini">(obrigatório se profilaxia da reinfecção ou prevenção da reativação viral por transplante)</div><table class="hb-sub"><tr><td>HBsAg<br/>${sel(dados.doadorHBsAg)}</td><td>Anti-HBc total<br/>${sel(dados.doadorAntiHBc)}</td></tr></table>`, { estilo: "grid-column: 4 / span 3;" })}
      ${box("24", "Receptor apresenta:", `<div class="hb-mini">(obrigatório se profilaxia da reinfecção ou prevenção da reativação viral por transplante)</div><table class="hb-sub"><tr><td>HBsAg<br/>${sel(dados.receptorHBsAg)}</td><td>Anti-HBc total<br/>${sel(dados.receptorAntiHBc)}</td></tr></table>`, { estilo: "grid-column: 7 / span 3;" })}
      ${box("25", "Risco de reativação viral:", `<div class="hb-mini">(obrigatório se Prevenção da reativação viral)</div>${sel(dados.riscoReativacao)}<div class="hb-num-in">26 - Momento da profilaxia da reinfecção:</div><div class="hb-mini">(obrigatório se Profilaxia da reinfecção pós-transplante hepático)</div>${sel(dados.momentoProfilaxia)}`, { estilo: "grid-column: 10 / span 3;" })}
    </div>

    <!-- LINHA 27/28 -->
    <div class="hb-grade">
      ${box("27", "Risco de reinfecção pós-transplante hepático:", `<div class="hb-mini">(obrigatório se Profilaxia da reinfecção pós-transp. hepático)</div>${sel(dados.riscoReinfeccao)}`, { estilo: "grid-column: 1 / span 6;" })}
      ${box("28", "Necessidade de terapia preemptiva por risco de reativação:", `<div class="hb-mini">(Se baixo risco – campo 25)</div>${sel(dados.terapiaPreemptiva)}`, { estilo: "grid-column: 7 / span 6;" })}
    </div>

    <!-- CAMPO 29 -->
    <div class="hb-grade">
      ${box("29", "Outros critérios para indicação de tratamento:", listaCheck(OUTROS_CRITERIOS_OPCOES, dados.outrosCriterios), { estilo: "grid-column: 1 / span 12;", obrig: true })}
    </div>

    <!-- CAMPO 30 -->
    <div class="hb-grade">
      ${box("30", "O paciente apresenta alguma das seguintes condições?", listaCheck(CONDICOES_OPCOES, dados.condicoes), { estilo: "grid-column: 1 / span 12;", obrig: true })}
    </div>

    <!-- LINHA 31/32/33 -->
    <div class="hb-grade">
      ${box("31", "Contraindicação ao uso de:", selMulti(dados.contraindicacao), { estilo: "grid-column: 1 / span 4;", obrig: true })}
      ${box("32", "Apresentou resistência ou falha terapêutica ao uso de:", selMulti(dados.resistenciaFalha), { estilo: "grid-column: 5 / span 4;", obrig: true })}
      ${box("33", "Uso prévio de:", selMulti(dados.usoPrevio), { estilo: "grid-column: 9 / span 4;", obrig: true })}
    </div>

    <!-- CAMPO 34 — REGISTRO DE DISPENSAÇÃO -->
    <div class="hb-barra">34 - Registro de dispensação<span class="hb-ob">*</span></div>
    <table class="hb-med-tab">
      <thead><tr><th class="hb-med-ck">&nbsp;</th><th class="hb-med-nome">Para preenchimento exclusivo do médico</th><th class="hb-med-disp">Para preenchimento exclusivo da farmácia</th></tr></thead>
      <tbody>${linhasMed}</tbody>
    </table>

    <!-- CAMPO 35 -->
    <div class="hb-grade">
      ${box("35", "Observação:", escapeHtml(dados.observacao), { estilo: "grid-column: 1 / span 12;", classe: "hb-alto" })}
    </div>

    <!-- CAMPO 36 -->
    <div class="hb-grade">
      ${box("36", "Prescritor", `<table class="hb-presc"><tr><td class="hb-assin"><div class="hb-assin-space">${selo || ""}</div><div class="hb-assin-l">${escapeHtml(solicitante.nomeProfissional || "")} — (Carimbo e assinatura)</div></td><td class="hb-presc-d">CRM: ${escapeHtml(reg)} &nbsp; RMS: ______ / UF: ____<br/>Data: ${escapeHtml(dados.dataSolicitacao)}</td></tr></table>`, { estilo: "grid-column: 1 / span 12;", obrig: true, classe: "hb-alto" })}
    </div>

    <!-- CAMPO 37 — RECIBO (farmácia) -->
    <div class="hb-barra hb-barra-fina">37 - Recibo (para preenchimento exclusivo pela farmácia)</div>
    <table class="hb-recibo">
      ${[1, 2, 3].map((i) => `
        <tr>
          <td><b>${i}ª dispensação</b><br/>Data: ___/___/_____<br/>Dispensação para ____ Dias<div class="hb-recibo-l">(Assinatura do farmacêutico)</div><div class="hb-recibo-l">(Assinatura do usuário)</div></td>
          <td><b>${i + 3}ª dispensação</b><br/>Data: ___/___/_____<br/>Dispensação para ____ Dias<div class="hb-recibo-l">(Assinatura do farmacêutico)</div><div class="hb-recibo-l">(Assinatura do usuário)</div></td>
        </tr>`).join("")}
    </table>
  </div>`;
}

// ------------------------------------------------------------
// ESTILOS
// ------------------------------------------------------------
const ESTILOS_HB = `
  /* AI_118: fontes aumentadas ~12% p/ legibilidade (Leonardo). Paddings/min-heights/
     line-height preservados p/ caber em 1 página (verificado por render headless). */
  .hb { font-family: Arial, Helvetica, sans-serif; color: #000; font-size: 8.0px; line-height: 1.1; }
  .hb-titulo { border: 1.5px solid #000; border-radius: 10px; text-align: center; padding: 2px 6px; margin-bottom: 2px; position: relative; }
  .hb-titulo-t { font-weight: 700; font-size: 12.5px; }
  .hb-titulo-s { font-size: 7.4px; font-weight: 600; }
  .hb-titulo-d { position: absolute; right: 8px; bottom: 2px; font-size: 7.4px; font-weight: 700; }
  .hb-grade { display: grid; grid-template-columns: repeat(12, 1fr); gap: 2px; margin-bottom: 2px; }
  .hb-box { position: relative; border: 1px solid #6b7785; border-radius: 8px; padding: 6px 5px 2px; min-height: 20px; background: #fff; }
  .hb-box.hb-alto { min-height: 32px; }
  .hb-num { position: absolute; top: -6px; left: 7px; background: #fff; padding: 0 3px; font-size: 7.4px; font-weight: 700; color: #1a1a1a; max-width: calc(100% - 10px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .hb-num-in { font-size: 7.4px; font-weight: 700; margin: 3px 0 1px; }
  .hb-ob { color: #b00; margin-left: 1px; }
  .hb-cont { margin-top: 1px; font-size: 9.5px; text-align: center; }
  .hb-cont b { font-weight: 700; }
  /* respostas em destaque (campos 7–10) */
  .hb-grande .hb-cont { font-size: 11.2px; }
  .hb-grande .hb-rad { font-size: 12.3px; }
  .hb-val { font-size: 14.5px; font-weight: 700; }
  /* destaque moderado (campos 17/18) */
  .hb-medio .hb-cont { font-size: 10.8px; }
  .hb-medio .hb-rad { font-size: 11.7px; }
  .hb-mini { font-size: 6.7px; font-weight: 400; font-style: italic; color: #333; line-height: 1.05; margin-bottom: 1px; }
  .hb-wrap { display: block; }
  .hb-rad { font-size: 10px; margin-right: 1.5px; vertical-align: -0.5px; }
  .hb-chk { font-size: 10px; margin-right: 1.5px; vertical-align: -0.5px; }
  /* CNS em caixas */
  .hb-celulas { display: flex; flex-wrap: wrap; gap: 1px; }
  .hb-cel { display: inline-block; width: 9px; height: 12px; border: 1px solid #9aa3ad; text-align: center; font-size: 7.8px; line-height: 12px; }
  /* subtabelas 23/24 */
  .hb-sub { width: 100%; border-collapse: collapse; }
  .hb-sub td { border: 1px solid #cdd3da; padding: 1px 3px; vertical-align: top; font-size: 7.6px; }
  /* listas 29/30 (texto corrido fica à esquerda) */
  .hb-lista { column-count: 2; column-gap: 10px; text-align: left; }
  .hb-lista-item { break-inside: avoid; margin-bottom: 1px; font-size: 7.6px; line-height: 1.08; text-align: left; }
  /* B289 (AI_127) — caixinha de marcação dos campos 29/30 (CSS, renderiza garantido) */
  .hb-cx2 { display: inline-block; width: 7px; height: 7px; border: 1px solid #000; margin-right: 3px; vertical-align: 0; }
  .hb-cx2.on { background: #000; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  /* barras */
  .hb-barra { border: 1px solid #000; border-radius: 6px; text-align: center; font-weight: 700; font-size: 9px; padding: 1px 4px; margin: 2px 0; }
  .hb-barra-fina { font-size: 8.3px; }
  /* tabela de medicamentos 34 */
  .hb-med-tab { width: 100%; border-collapse: collapse; border: 1px solid #6b7785; border-radius: 8px; overflow: hidden; margin-bottom: 2px; }
  .hb-med-tab th, .hb-med-tab td { border: 1px solid #9aa3ad; padding: 1px 4px; font-size: 7.7px; vertical-align: top; }
  .hb-med-tab thead th { background: #eef1f4; font-weight: 700; text-align: center; }
  .hb-med-ck { width: 16px; text-align: center; }
  .hb-med-nome { width: 46%; }
  .hb-med-poso { font-weight: 400; font-size: 7.2px; color: #222; }
  .hb-med-disp-l { font-size: 6.9px; color: #444; margin-top: 1px; }
  /* prescritor 36 — espaço de assinatura ACIMA da linha */
  .hb-presc { width: 100%; border-collapse: collapse; }
  .hb-presc td { vertical-align: bottom; padding: 1px 4px; font-size: 8.3px; }
  .hb-assin { width: 58%; text-align: center; }
  .hb-assin-space { min-height: 20px; }
  .hb-assin-l { border-top: 1px solid #000; font-size: 6.9px; color: #444; padding-top: 1px; }
  .hb-presc-d { text-align: left; }
  /* recibo 37 */
  .hb-recibo { width: 100%; border-collapse: collapse; }
  .hb-recibo td { border: 1px solid #888; padding: 2px 4px; width: 50%; font-size: 7.6px; vertical-align: top; }
  .hb-recibo-l { font-size: 6.7px; color: #444; border-top: 1px solid #aaa; margin-top: 5px; }
`;

// ------------------------------------------------------------
// API PÚBLICA
// ------------------------------------------------------------
/** Fragmento p/ embutir no MESMO documento da receita (folha extra). */
export function montarFragmentoHepatiteB(
  paciente: Paciente,
  solicitante: SolicitanteHepatiteB,
  dados: DadosHepatiteB,
  // B289 (AI_127) — selo ICP-Brasil (compacto) no campo 36, só no PDF assinado.
  selo?: string
): { estilos: string; corpo: string } {
  const estilos = `
    @page hepb { size: A4 portrait; margin: 5mm; }
    .folha-hepb { page: hepb; page-break-before: always; }
    ${ESTILOS_HB}
  `;
  const corpo = `<div class="folha-hepb">${corpoHepatiteB(paciente, solicitante, dados, selo)}</div>`;
  return { estilos, corpo };
}

/** Documento AUTÔNOMO da ficha de Hepatite B (sem a receita comum). */
function montarHTML(
  paciente: Paciente,
  solicitante: SolicitanteHepatiteB,
  dados: DadosHepatiteB
): string {
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>Solicitação de Medicamentos — Hepatite B — ${escapeHtml(paciente.nome)}</title>
  <style>
    @page { size: A4 portrait; margin: 5mm; }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; background: #e9e9e9; }
    .barra-botoes { text-align: center; padding: 14px; }
    .hb { width: 200mm; margin: 0 auto 20px; background: #fff; padding: 4mm; }
    ${ESTILOS_HB}
    @media print {
      body { background: #fff; }
      .hb { width: auto; margin: 0; padding: 0; }
      .barra-botoes { display: none; }
    }
  </style>
</head>
<body>
  <div class="barra-botoes">
    <button onclick="window.print()" style="padding: 10px 32px; background: #2563eb; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; margin-right: 8px;">
      🖨️ Imprimir / Salvar PDF
    </button>
    <button onclick="window.close()" style="padding: 10px 32px; background: #6b7280; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;">
      ✕ Fechar
    </button>
  </div>
  ${corpoHepatiteB(paciente, solicitante, dados)}
</body>
</html>`;
}

export function gerarFichaHepatiteB(
  paciente: Paciente,
  solicitante: SolicitanteHepatiteB,
  dados: DadosHepatiteB
): void {
  abrirPDFEmNovaJanela(montarHTML(paciente, solicitante, dados));
}
